import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { iotDataApi, listAsroTrackConnectors, listPostgresConnectors } from '../../services/iotDataApi';
import SimpleLineChart from './SimpleLineChart';
import VibrationAnalysisPanel from './VibrationAnalysisPanel';

const TABS = [
  { key: 'raw', label: 'Raw Data', table: 'sensor_reading_raw', dataset: null },
  { key: 'aggregated', label: 'Aggregated Data', table: 'sensor_reading_aggregated', dataset: 'aggregated' },
  { key: 'feature', label: 'Feature Description', table: 'feature_definition', dataset: 'feature_definition' },
  { key: 'calculated', label: 'Calculated Field', table: 'sensor_feature', dataset: 'sensor_feature' },
  { key: 'baseline', label: 'Baseline', table: 'baseline', dataset: 'baseline' },
  { key: 'trends', label: 'Trends', table: 'feature_trend', dataset: 'feature_trend' },
  {
    key: 'analytical',
    label: 'Analytical Context',
    table: 'sp_get_asset_ai_analytical_context',
    dataset: 'analytical_context',
  },
  {
    key: 'vibration',
    label: 'Vibration Analysis',
    table: 'vibration_analysis',
    dataset: null,
  },
];

/** Grid fetches in blocks of 100 per page request. */
const GRID_PAGE_SIZE = 100;
const GRID_PAGE_SIZE_OPTIONS = [100];

/** Chart buckets for long From/To so Apply stays light (grid still exact pages of 100). */
function suggestChartBucketMinutes(from, to) {
  const start = dayjs(from);
  const end = dayjs(to);
  if (!start.isValid() || !end.isValid()) return 0;
  const hours = Math.max(0, end.diff(start, 'hour', true));
  if (hours <= 24) return 0;
  if (hours <= 24 * 7) return 5;
  if (hours <= 24 * 30) return 15;
  if (hours <= 24 * 90) return 60;
  return 240;
}

const DATASET_COLUMNS = {
  aggregated: [
    { field: 'periodStartLabel', headerName: 'Period Start', flex: 1.1, minWidth: 150 },
    { field: 'periodEndLabel', headerName: 'Period End', flex: 1.1, minWidth: 150 },
    { field: 'granularity', headerName: 'Granularity', flex: 0.7, minWidth: 100 },
    { field: 'avg', headerName: 'Avg', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'min', headerName: 'Min', flex: 0.5, minWidth: 70, type: 'number' },
    { field: 'max', headerName: 'Max', flex: 0.5, minWidth: 70, type: 'number' },
    { field: 'stddev', headerName: 'StdDev', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'p95', headerName: 'P95', flex: 0.5, minWidth: 70, type: 'number' },
    { field: 'sample_count', headerName: 'Samples', flex: 0.6, minWidth: 90, type: 'number' },
  ],
  feature: [
    { field: 'feature_id', headerName: 'Feature ID', flex: 0.7, minWidth: 90 },
    { field: 'feature_code', headerName: 'Code', flex: 0.9, minWidth: 120 },
    { field: 'feature_name', headerName: 'Name', flex: 1.1, minWidth: 140 },
    { field: 'category', headerName: 'Category', flex: 0.9, minWidth: 120 },
    { field: 'formula_ref', headerName: 'Formula', flex: 1.2, minWidth: 160 },
    { field: 'applicable_sensor_type', headerName: 'Sensor Type', flex: 0.9, minWidth: 110 },
  ],
  calculated: [
    { field: 'tsLabel', headerName: 'Timestamp', flex: 1.1, minWidth: 150 },
    { field: 'sensor_id', headerName: 'Sensor', flex: 0.7, minWidth: 100 },
    { field: 'feature_id', headerName: 'Feature', flex: 0.7, minWidth: 90 },
    { field: 'value', headerName: 'Value', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'z_score', headerName: 'Z-Score', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'pct_change', headerName: '% Change', flex: 0.6, minWidth: 90, type: 'number' },
    { field: 'window_seconds', headerName: 'Window (s)', flex: 0.7, minWidth: 100, type: 'number' },
    { field: 'state_id', headerName: 'State', flex: 0.8, minWidth: 110 },
  ],
  baseline: [
    { field: 'baseline_id', headerName: 'Baseline ID', flex: 0.8, minWidth: 100 },
    { field: 'sensor_id', headerName: 'Sensor', flex: 0.7, minWidth: 100 },
    { field: 'asset_id', headerName: 'Asset', flex: 0.7, minWidth: 100 },
    { field: 'state_id', headerName: 'State', flex: 0.8, minWidth: 110 },
    { field: 'version', headerName: 'Ver', flex: 0.4, minWidth: 60, type: 'number' },
    { field: 'mean', headerName: 'Mean', flex: 0.5, minWidth: 70, type: 'number' },
    { field: 'median', headerName: 'Median', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'stddev', headerName: 'StdDev', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'p95', headerName: 'P95', flex: 0.5, minWidth: 70, type: 'number' },
    { field: 'min', headerName: 'Min', flex: 0.45, minWidth: 70, type: 'number' },
    { field: 'max', headerName: 'Max', flex: 0.45, minWidth: 70, type: 'number' },
  ],
  trends: [
    { field: 'trend_id', headerName: 'Trend ID', flex: 0.7, minWidth: 90 },
    { field: 'sensor_id', headerName: 'Sensor', flex: 0.7, minWidth: 100 },
    { field: 'feature_id', headerName: 'Feature', flex: 0.7, minWidth: 90 },
    { field: 'window_days', headerName: 'Window (d)', flex: 0.7, minWidth: 100, type: 'number' },
    { field: 'slope', headerName: 'Slope', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'r_squared', headerName: 'R²', flex: 0.5, minWidth: 70, type: 'number' },
    { field: 'trend_direction', headerName: 'Direction', flex: 0.8, minWidth: 100 },
    { field: 'rate_of_change', headerName: 'Rate of Change', flex: 0.9, minWidth: 120, type: 'number' },
  ],
  // ≤10 rows from sp_get (same contract as Sync analytical-context API).
  // No Measured At / Scenario / Asset / Sensor — Feature = feature_code + Feature name.
  analytical: [
    { field: 'feature_code', headerName: 'Feature', flex: 0.9, minWidth: 120 },
    { field: 'feature_name', headerName: 'Feature name', flex: 1.1, minWidth: 140 },
    { field: 'feature_description', headerName: 'Description', flex: 1.4, minWidth: 160 },
    { field: 'current_value', headerName: 'Current', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'baseline_value', headerName: 'Baseline', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'unit', headerName: 'Unit', flex: 0.45, minWidth: 60 },
    { field: 'deviation_pct', headerName: 'Dev %', flex: 0.5, minWidth: 70, type: 'number' },
    { field: 'z_score', headerName: 'Z-Score', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'threshold_status', headerName: 'Status', flex: 0.7, minWidth: 90 },
    { field: 'trend_direction', headerName: 'Trend', flex: 0.7, minWidth: 100 },
    { field: 'agg_min', headerName: 'Agg Min', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'agg_avg', headerName: 'Agg Avg', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'agg_max', headerName: 'Agg Max', flex: 0.55, minWidth: 80, type: 'number' },
    { field: 'operating_state', headerName: 'State', flex: 0.7, minWidth: 100 },
  ],
};

const PRESETS = [
  { key: '1h', label: 'Last Hour', hours: 1 },
  { key: '24h', label: 'Last 24 Hours', hours: 24 },
  { key: '7d', label: 'Last 7 Days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 Days', hours: 24 * 30 },
];

const formatRange = (hours) => {
  const to = dayjs();
  const from = to.subtract(hours, 'hour');
  return {
    from: from.format('YYYY-MM-DDTHH:mm'),
    to: to.format('YYYY-MM-DDTHH:mm'),
  };
};

/** Compact quality pill — only render DB values (no invented "Good") */
const qualityChip = (value) => {
  if (value == null || value === '' || value === '—') {
    return (
      <Typography component="span" variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  const raw = String(value);
  const lower = raw.toLowerCase();
  const tone =
    lower.includes('bad') || lower.includes('fail')
      ? { color: '#c62828', border: '#ef9a9a', bg: '#ffebee' }
      : lower.includes('warn')
        ? { color: '#ef6c00', border: '#ffcc80', bg: '#fff3e0' }
        : { color: '#2e7d32', border: '#a5d6a7', bg: '#e8f5e9' };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 20,
        maxHeight: 20,
        px: 0.75,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: tone.border,
        bgcolor: tone.bg,
        color: tone.color,
        fontSize: '0.65rem',
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
      }}
    >
      {raw}
    </Box>
  );
};

const cellOrDash = (v) => (v == null || v === '' ? '—' : v);

/** Shared interactive control height — every filter control must match this exactly */
const CTRL_H = 34;
const CTRL_H_PX = `${CTRL_H}px`;
const compactFieldSx = {
  boxSizing: 'border-box',
  '& .MuiInputBase-root': {
    height: CTRL_H_PX,
    minHeight: `${CTRL_H}px !important`,
    maxHeight: CTRL_H_PX,
    fontSize: '0.75rem',
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input': {
    py: '0 !important',
    height: CTRL_H_PX,
    fontSize: '0.75rem',
    boxSizing: 'border-box',
  },
  '& .MuiSelect-select': {
    py: '0 !important',
    display: 'flex',
    alignItems: 'center',
    height: CTRL_H_PX,
    boxSizing: 'border-box',
  },
  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
};
/** datetime-local often ignores height unless root + input are both locked */
const timeFieldSx = {
  width: '100%',
  height: CTRL_H_PX,
  minHeight: CTRL_H_PX,
  maxHeight: CTRL_H_PX,
  boxSizing: 'border-box',
  '& .MuiOutlinedInput-root': {
    height: CTRL_H_PX,
    minHeight: `${CTRL_H}px !important`,
    maxHeight: CTRL_H_PX,
    fontSize: '0.75rem',
    boxSizing: 'border-box',
    alignItems: 'center',
  },
  '& .MuiOutlinedInput-input': {
    py: '0 !important',
    height: CTRL_H_PX,
    maxHeight: CTRL_H_PX,
    fontSize: '0.75rem',
    lineHeight: CTRL_H_PX,
    boxSizing: 'border-box',
  },
  '& input[type="datetime-local"]': {
    height: `${CTRL_H}px !important`,
    maxHeight: `${CTRL_H}px !important`,
    py: '0 !important',
    boxSizing: 'border-box',
  },
};
const ctrlBtnSx = {
  textTransform: 'none',
  fontSize: '0.72rem',
  height: CTRL_H_PX,
  minHeight: `${CTRL_H}px !important`,
  maxHeight: CTRL_H_PX,
  py: 0,
  lineHeight: 1.2,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
};
/**
 * Label row above every row-2 control (visible or invisible).
 * Same height for all columns → equal total stack height (the red-line length).
 */
const FILTER_LABEL_SX = {
  height: 16,
  m: 0,
  mb: '2px',
  p: 0,
  flexShrink: 0,
  lineHeight: '16px',
  fontSize: '0.7rem',
  color: 'text.secondary',
  display: 'block',
  overflow: 'hidden',
};
const FILTER_LABEL_SLOT_SX = {
  ...FILTER_LABEL_SX,
  visibility: 'hidden',
  userSelect: 'none',
};

const IoTDataView = () => {
  const [connectors, setConnectors] = useState([]);
  const [ctdId, setCtdId] = useState('');
  const [pgConnectors, setPgConnectors] = useState([]);
  const [dbCtdId, setDbCtdId] = useState('');
  const [plants, setPlants] = useState([]);
  const [plantId, setPlantId] = useState('');
  const [assets, setAssets] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [assetId, setAssetId] = useState('');
  const [sensorId, setSensorId] = useState('');
  const [preset, setPreset] = useState('custom');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tab, setTab] = useState('raw');
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [processingRaw, setProcessingRaw] = useState(false);
  const [chart, setChart] = useState(null);
  const [rawRows, setRawRows] = useState([]);
  const [rawTotal, setRawTotal] = useState(0);
  const [tabRows, setTabRows] = useState([]);
  const [tabTotal, setTabTotal] = useState(0);
  const [tabTable, setTabTable] = useState('');
  const [baselineFeatures, setBaselineFeatures] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: GRID_PAGE_SIZE });
  const [selectedRow, setSelectedRow] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [chartExpanded, setChartExpanded] = useState(true);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  /** Calculated Field chart: which feature_id to plot from loaded rows */
  const [calcChartFeatureId, setCalcChartFeatureId] = useState('');
  /** Analytical Context: view recreated on Apply for chosen From/To */
  const [analyticalViewMeta, setAnalyticalViewMeta] = useState(null);
  /** Named filter combinations saved in SyncTrack tenant table */
  const [filterPresets, setFilterPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [error, setError] = useState('');
  const hasAppliedRef = useRef(false);
  /** After restoring last-applied filters, reload chart/grid once sensors settle. */
  const pendingAutoApplyRef = useRef(false);
  /** Expected ids from last-applied row — wait until cascade matches before reload. */
  const lastAppliedTargetRef = useRef(null);
  const restoreAttemptRef = useRef(0);
  /** Ignore stale API responses when tab/filters change quickly. */
  const loadGenRef = useRef(0);

  const reloadFilterPresets = useCallback(async () => {
    try {
      const { data } = await iotDataApi.listFilterPresets();
      setFilterPresets(data?.items || []);
      return data?.lastApplied || null;
    } catch {
      setFilterPresets([]);
      return null;
    }
  }, []);

  /** Fill filter fields from a saved / last-applied snapshot. */
  const hydrateFiltersFromPreset = useCallback((preset, { selectInDropdown = false, toastLabel, autoApply = false } = {}) => {
    if (!preset) return;
    if (selectInDropdown && preset.id) setSelectedPresetId(preset.id);
    else if (!selectInDropdown) setSelectedPresetId('');
    if (preset.trackCtdId) setCtdId(preset.trackCtdId);
    if (preset.dbCtdId) setDbCtdId(preset.dbCtdId);
    if (preset.plantId) setPlantId(String(preset.plantId));
    if (preset.assetId) setAssetId(String(preset.assetId));
    // Legacy "All sensors" sentinel is gone — let sensor list pick the first meter.
    const restoredSensor =
      preset.sensorId && String(preset.sensorId) !== '__ALL_SENSORS__'
        ? String(preset.sensorId)
        : '';
    if (restoredSensor) setSensorId(restoredSensor);
    else setSensorId('');
    setDateFrom(preset.dateFrom || '');
    setDateTo(preset.dateTo || '');
    if (preset.tab && TABS.some((t) => t.key === preset.tab)) setTab(preset.tab);
    setPreset('custom');
    hasAppliedRef.current = false;
    pendingAutoApplyRef.current = Boolean(autoApply);
    restoreAttemptRef.current = 0;
    if (autoApply) {
      lastAppliedTargetRef.current = {
        plantId: preset.plantId ? String(preset.plantId) : '',
        assetId: preset.assetId ? String(preset.assetId) : '',
        sensorId: restoredSensor,
        dateFrom: preset.dateFrom || '',
        dateTo: preset.dateTo || '',
        tab: preset.tab || 'raw',
      };
    } else {
      lastAppliedTargetRef.current = null;
    }
    if (toastLabel) toast.info(toastLabel);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [trackList, pgList, lastApplied] = await Promise.all([
          listAsroTrackConnectors(),
          listPostgresConnectors(),
          reloadFilterPresets(),
        ]);
        if (cancelled) return;
        setConnectors(trackList);
        setPgConnectors(pgList);

        if (lastApplied?.trackCtdId || lastApplied?.sensorId || lastApplied?.dateFrom) {
          hydrateFiltersFromPreset(lastApplied, { selectInDropdown: false, autoApply: true });
        } else {
          const trackPick =
            trackList.find((c) => String(c.status || '').toLowerCase() === 'connected') ||
            trackList[0];
          if (trackPick) setCtdId(trackPick.id);
          const dbPick =
            pgList.find((c) => /iot/i.test(`${c.mcName} ${c.description}`)) ||
            pgList.find((c) => String(c.status || '').toLowerCase() === 'connected') ||
            pgList[0];
          if (dbPick) setDbCtdId(dbPick.id);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load connectors');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilterPreset = (preset) => {
    hydrateFiltersFromPreset(preset, {
      selectInDropdown: true,
      toastLabel: `Loaded filter “${preset?.name}” — click Apply to load data`,
    });
  };

  const defaultPresetName = () => {
    const assetLabel =
      assets.find((a) => String(a.asstId) === String(assetId))?.asstTag || assetId || 'asset';
    const sensorLabel =
      sensors.find((s) => String(s.astioId) === String(sensorId))?.astioExMeterCode ||
      sensors.find((s) => String(s.astioId) === String(sensorId))?.astioDeviceId ||
      sensorId ||
      'sensor';
    const fromShort = dateFrom ? dayjs(dateFrom).format('DD-MMM HH:mm') : '…';
    const toShort = dateTo ? dayjs(dateTo).format('DD-MMM HH:mm') : '…';
    return `${assetLabel} / ${sensorLabel} · ${fromShort} → ${toShort}`;
  };

  const openSavePresetDialog = () => {
    // Always suggest from current asset / sensor / From–To (not an older Saved name)
    setSavePresetName(defaultPresetName());
    setSavePresetOpen(true);
  };

  const confirmSavePreset = async () => {
    const trimmed = String(savePresetName || '').trim() || defaultPresetName();
    setSavingPreset(true);
    try {
      const { data } = await iotDataApi.saveFilterPreset({
        name: trimmed,
        trackCtdId: ctdId,
        dbCtdId,
        plantId,
        assetId,
        sensorId,
        dateFrom,
        dateTo,
        tab,
      });
      await reloadFilterPresets();
      if (data?.id) setSelectedPresetId(data.id);
      setSavePresetOpen(false);
      toast.success(`Saved “${trimmed}”`);
    } catch (e) {
      toast.error(e.message || 'Failed to save filters');
      setError(e.message || 'Failed to save filters');
    } finally {
      setSavingPreset(false);
    }
  };

  const deleteSelectedPreset = async () => {
    if (!selectedPresetId) {
      toast.warn('Select a saved filter first');
      return;
    }
    const preset = filterPresets.find((p) => p.id === selectedPresetId);
    if (!window.confirm(`Delete saved filter “${preset?.name || selectedPresetId}”?`)) return;
    try {
      await iotDataApi.deleteFilterPreset(selectedPresetId);
      setSelectedPresetId('');
      await reloadFilterPresets();
      toast.success('Deleted');
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const loadPlants = useCallback(async () => {
    if (!ctdId) return;
    setLoadingMeta(true);
    setError('');
    try {
      const { data } = await iotDataApi.listPlants({ ctdId, offset: 0, limit: 50 });
      const items = data?.items || [];
      setPlants(items);
      setPlantId((prev) => {
        const match = items.find((p) => String(p.plantId) === String(prev));
        if (match) return String(match.plantId);
        return items.length ? String(items[0].plantId) : '';
      });
    } catch (e) {
      setPlants([]);
      setPlantId('');
      setError(e.message || 'Failed to load plants');
      toast.error(e.message || 'Failed to load plants');
    } finally {
      setLoadingMeta(false);
    }
  }, [ctdId]);

  const loadAssets = useCallback(async () => {
    if (!ctdId || !plantId) {
      setAssets([]);
      setAssetId('');
      return;
    }
    setLoadingMeta(true);
    setError('');
    try {
      const { data } = await iotDataApi.listAssets({
        ctdId,
        plantId,
        offset: 0,
        limit: 200,
      });
      const items = data?.items || [];
      setAssets(items);
      setAssetId((prev) => {
        const match = items.find((a) => String(a.asstId) === String(prev));
        if (match) return String(match.asstId);
        return items.length ? String(items[0].asstId) : '';
      });
    } catch (e) {
      setAssets([]);
      setAssetId('');
      setError(e.message || 'Failed to load assets');
      toast.error(e.message || 'Failed to load assets');
    } finally {
      setLoadingMeta(false);
    }
  }, [ctdId, plantId]);

  useEffect(() => {
    if (ctdId) loadPlants();
  }, [ctdId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ctdId && plantId) loadAssets();
  }, [ctdId, plantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ctdId || !assetId) {
      setSensors([]);
      setSensorId('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const { data } = await iotDataApi.listSensors({
          ctdId,
          assetId,
          metersOnly: true,
          limit: 100,
        });
        if (cancelled) return;
        const items = data?.items || [];
        setSensors(items);
        setSensorId((prev) => {
          const match = items.find((s) => String(s.astioId) === String(prev));
          if (match) return String(match.astioId);
          return items.length ? String(items[0].astioId) : '';
        });
      } catch (e) {
        if (!cancelled) {
          setSensors([]);
          setSensorId('');
          toast.error(e.message || 'Failed to load sensors');
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctdId, assetId]);

  const applyPreset = (key) => {
    // User changed the range manually — stop fighting last-applied auto-load
    pendingAutoApplyRef.current = false;
    lastAppliedTargetRef.current = null;
    hasAppliedRef.current = false;
    setPreset(key);
    const found = PRESETS.find((p) => p.key === key);
    if (!found) return;
    const range = formatRange(found.hours);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  /** Send From/To as UTC wall-clock (append Z). Matches timestamptz …+00 in DB. */
  const toIsoParam = (localValue) => {
    if (!localValue) return undefined;
    const d = dayjs(localValue);
    return d.isValid() ? `${d.format('YYYY-MM-DDTHH:mm:ss')}Z` : localValue;
  };

  const resolveTelemetryIds = useCallback(() => {
    const assetMeta = assets.find((a) => String(a.asstId) === String(assetId));
    const sensorMeta = sensors.find((s) => String(s.astioId) === String(sensorId));
    return {
      assetMeta,
      sensorMeta,
      telemetryAssetId: assetMeta?.asstTag || assetId,
      telemetrySensorId:
        sensorMeta?.astioDeviceId || sensorMeta?.astioExMeterCode || sensorId,
    };
  }, [assets, sensors, assetId, sensorId]);

  const mapAnalyticalRows = (items) =>
    items.map((row, idx) => {
      const id =
        row.source_row_id ||
        `${row.feature_code || row.feature_id}-${row.measured_at}-${idx}`;
      return {
        id: String(id),
        ...row,
        feature_description:
          row.feature_description || row.feature_name || '—',
        min: row.min ?? row.agg_min ?? null,
        max: row.max ?? row.agg_max ?? null,
        measuredLabel: row.measured_at
          ? dayjs(row.measured_at).format('YYYY-MM-DD HH:mm:ss')
          : '—',
      };
    });

  /**
   * @param rebuildView - true on Apply (analytical tab loads via sp_get for From/To).
   *   Refresh / restore / pagination: also sp_get when on analytical tab.
   */
  const loadTelemetry = useCallback(
    async (
      page = paginationModel.page,
      pageSize = paginationModel.pageSize,
      activeTab = tab,
      { rebuildView = false } = {}
    ) => {
      if (!assetId) {
        toast.warn('Select an asset first');
        return;
      }
      if (activeTab !== 'analytical' && !sensorId) {
        toast.warn('Select a sensor first');
        return;
      }
      if (!dbCtdId) {
        toast.warn('Select the IoT database connector first');
        return;
      }
      if (rebuildView && (!dateFrom || !dateTo)) {
        toast.warn('Set From and To, then Apply');
        return;
      }
      const loadGen = ++loadGenRef.current;
      const isStale = () => loadGen !== loadGenRef.current;
      setLoadingData(true);
      setError('');
      try {
        const { telemetryAssetId, telemetrySensorId, assetMeta, sensorMeta } =
          resolveTelemetryIds();
        // IoT DB keys are AsroTrack tag / meter code (e.g. 1001 / 5001), not asstId / astioId
        if (!assetMeta?.asstTag) {
          toast.warn('Asset details still loading — try Apply again in a moment');
          setLoadingData(false);
          return;
        }
        // Analytical Context is asset-only (no sensor filter). Other tabs need a sensor.
        if (
          activeTab !== 'analytical' &&
          !(sensorMeta?.astioDeviceId || sensorMeta?.astioExMeterCode)
        ) {
          toast.warn('Asset/sensor details still loading — try Apply again in a moment');
          setLoadingData(false);
          return;
        }
        const params = {
          assetId: telemetryAssetId,
          sensorId: telemetrySensorId,
          dbCtdId,
          from: toIsoParam(dateFrom),
          to: toIsoParam(dateTo),
          dateFrom: toIsoParam(dateFrom),
          dateTo: toIsoParam(dateTo),
        };
        const tabMeta = TABS.find((t) => t.key === activeTab) || TABS[0];

        // Vibration Analysis panel loads its own APIs (overview / trends / waveforms / spectra).
        if (activeTab === 'vibration') {
          if (isStale()) return;
          setTabTable(tabMeta.table);
          setTabRows([]);
          setTabTotal(0);
          setChart(null);
          setRawRows([]);
          setRawTotal(0);
          setBaselineFeatures([]);
          setLoadingData(false);
          return;
        }

        // Analytical Context: always asset-wide (blank sensorId = all features for the asset).
        const analyticalParams = {
          ...params,
          sensorId: '',
        };
        const runAnalyticalSpGet = () => {
          if (!rebuildView || !dateFrom || !dateTo) return Promise.resolve(null);
          return iotDataApi
            .getDataset({
              ...analyticalParams,
              dataset: 'analytical_context',
              rebuild: true,
              page: activeTab === 'analytical' ? page + 1 : 1,
              pageSize: activeTab === 'analytical' ? pageSize : 100,
            })
            .then((res) => res.data)
            .catch((err) => {
              toast.error(err.message || 'Failed to load analytical features (sp_get)');
              return null;
            });
        };

        if (activeTab === 'raw') {
          const bucketMinutes = suggestChartBucketMinutes(dateFrom, dateTo);
          // First paint: page of 100 + bucketed chart only. COUNT only on page 0 / Apply.
          const [chartRes, rawRes] = await Promise.all([
            iotDataApi.getChart({ ...params, bucketMinutes }),
            iotDataApi.getRaw({
              ...params,
              page: page + 1,
              pageSize,
              includeTotal: page === 0,
            }),
          ]);
          if (isStale()) return;
          const chartData = chartRes.data || null;
          setChart(chartData);
          const items = rawRes.data?.items || [];
          // Newest-first for the grid. Same-second duplicates get ms so they are distinguishable.
          const secondCounts = {};
          items.forEach((row) => {
            const key = row.timestamp ? dayjs(row.timestamp).format('YYYY-MM-DD HH:mm:ss') : '';
            if (key) secondCounts[key] = (secondCounts[key] || 0) + 1;
          });
          setRawRows(
            items.map((row, idx) => {
              const ts = row.timestamp;
              const d = ts ? dayjs(ts) : null;
              const secondKey = d?.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '';
              const showMs = secondKey && secondCounts[secondKey] > 1;
              return {
                id: `${ts}-${idx}`,
                timestamp: ts,
                timestampLabel: d?.isValid()
                  ? d.format(showMs ? 'YYYY-MM-DD HH:mm:ss.SSS' : 'YYYY-MM-DD HH:mm:ss')
                  : '—',
                asset: row.asset ?? null,
                sensor: row.sensor ?? null,
                measurementType: row.measurementType ?? null,
                value: row.value,
                units: row.units ?? null,
                quality: row.quality ?? null,
                waveformRef: row.waveformRef ?? null,
              };
            })
          );
          setRawTotal((prev) => {
            if (rawRes.data?.totalExact) return rawRes.data.total || items.length;
            if (page > 0 && prev > 0) return prev;
            return rawRes.data?.total || items.length;
          });
          setTabTable(tabMeta.table);
          setBaselineFeatures([]);
          if (chartData?.source === 'demo') {
            toast.info('Telemetry returned demo data — check IoT DB connector / table mapping');
          }
          // After raw paint: warm analytical meta via sp_get (does not block Raw).
          if (rebuildView) {
            runAnalyticalSpGet().then((analyticalData) => {
              if (isStale() || !analyticalData?.viewCreated) return;
              setAnalyticalViewMeta(analyticalData.viewRange || null);
            });
          }
        } else if (activeTab === 'analytical') {
          let data = await runAnalyticalSpGet();
          if (isStale()) return;
          if (!data) {
            // Refresh / pagination: still sp_get with From/To (no view rebuild).
            const readRes = await iotDataApi.getDataset({
              ...analyticalParams,
              dataset: 'analytical_context',
              rebuild: false,
              page: page + 1,
              pageSize,
            });
            if (isStale()) return;
            data = readRes.data;
            if (!data) {
              setTabRows([]);
              setTabTotal(0);
              return;
            }
          }
          const items = data?.items || [];
          setTabTable(data?.table || tabMeta.table);
          setTabTotal(data?.total || items.length);
          setBaselineFeatures([]);
          setAnalyticalViewMeta(data.viewRange || null);
          setChart(null);
          setRawRows([]);
          setRawTotal(0);
          setTabRows(mapAnalyticalRows(items));
        } else {
          const { data } = await iotDataApi.getDataset({
            ...params,
            dataset: tabMeta.dataset,
            page: page + 1,
            pageSize,
          });
          if (isStale()) return;
          const items = data?.items || [];
          setTabTable(tabMeta.table);
          setTabTotal(data?.total || items.length);
          setBaselineFeatures(
            activeTab === 'baseline' ? data?.baselineFeatures || [] : []
          );
          setChart(null);
          setRawRows([]);
          setRawTotal(0);
          setTabRows(
            items.map((row, idx) => {
              const id =
                (activeTab === 'trends' && row.trend_id) ||
                (activeTab === 'baseline' && row.baseline_id) ||
                (activeTab === 'aggregated' && row.agg_id) ||
                (activeTab === 'calculated' && row.feature_value_id) ||
                (activeTab === 'feature' && row.feature_id) ||
                row.trend_id ||
                row.baseline_id ||
                row.feature_value_id ||
                row.agg_id ||
                `${activeTab}-${idx}-${row.timestamp || row.period_start || row.measured_at || ''}`;
              return {
                id: String(id),
                ...row,
                periodStartLabel: row.period_start
                  ? dayjs(row.period_start).format('YYYY-MM-DD HH:mm')
                  : '—',
                periodEndLabel: row.period_end
                  ? dayjs(row.period_end).format('YYYY-MM-DD HH:mm')
                  : '—',
                tsLabel: (row.timestamp || row.ts)
                  ? dayjs(row.timestamp || row.ts).format('YYYY-MM-DD HH:mm:ss')
                  : '—',
                measuredLabel: row.measured_at
                  ? dayjs(row.measured_at).format('YYYY-MM-DD HH:mm:ss')
                  : '—',
              };
            })
          );
          if (rebuildView) {
            runAnalyticalSpGet().then((analyticalData) => {
              if (isStale() || !analyticalData?.viewCreated) return;
              setAnalyticalViewMeta(analyticalData.viewRange || null);
            });
          }
        }
        if (!isStale()) setSelectedRow(null);
      } catch (e) {
        if (isStale()) return;
        setError(e.message || 'Failed to load data');
        toast.error(e.message || 'Failed to load data');
      } finally {
        if (!isStale()) setLoadingData(false);
      }
    },
    [
      assetId,
      sensorId,
      ctdId,
      plantId,
      dbCtdId,
      dateFrom,
      dateTo,
      tab,
      resolveTelemetryIds,
      paginationModel.page,
      paginationModel.pageSize,
    ]
  );

  const onApply = () => {
    hasAppliedRef.current = true;
    pendingAutoApplyRef.current = false;
    // Persist filters; analytical sp_get runs on Apply (and on analytical refresh)
    iotDataApi
      .saveLastAppliedFilters({
        trackCtdId: ctdId,
        dbCtdId,
        plantId,
        assetId,
        sensorId,
        dateFrom,
        dateTo,
        tab,
      })
      .catch(() => {});
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
    loadTelemetry(0, paginationModel.pageSize, tab, { rebuildView: true });
  };

  /** Incremental: raw from last aggregated hour through now → agg / features / trends (all assets). */
  const onProcessFromRaw = async () => {
    if (!dbCtdId) {
      toast.warn('Select the IoT database connector first');
      return;
    }
    setProcessingRaw(true);
    try {
      const { data } = await iotDataApi.processFromRaw({ dbCtdId, fullRebuild: false });
      const t = data?.tables || {};
      const mode = data?.mode || 'incremental';
      const newRaw = data?.newRawRows;
      toast.success(
        `${data?.database || 'IoT DB'} (${mode}` +
          (newRaw != null ? `, +${newRaw} raw` : '') +
          `): agg ${t.sensor_reading_aggregated ?? '—'}, ` +
          `features ${t.sensor_feature ?? '—'}, baseline ${t.baseline_feature ?? '—'}, ` +
          `trends ${t.feature_trend ?? '—'}` +
          (data?.note ? ` — ${data.note}` : '')
      );
      if (hasAppliedRef.current) {
        loadTelemetry(0, paginationModel.pageSize, tab, { rebuildView: true });
      }
    } catch (e) {
      toast.error(e.message || 'Process sensor data failed');
    } finally {
      setProcessingRaw(false);
    }
  };

  // Restore after refresh: show last filters + load data once asset/sensor meta resolves
  // (asstTag / deviceId). Querying with raw asstId/astioId alone returns 0 IoT rows.
  useEffect(() => {
    if (!pendingAutoApplyRef.current) return;
    if (!ctdId || !dbCtdId || loadingMeta) return;
    const restoreTabHint =
      lastAppliedTargetRef.current?.tab &&
      TABS.some((x) => x.key === lastAppliedTargetRef.current.tab)
        ? lastAppliedTargetRef.current.tab
        : tab;
    // Analytical Context is asset-only; other tabs still need a concrete sensor.
    if (restoreTabHint !== 'analytical' && !sensorId) return;
    const target = lastAppliedTargetRef.current;
    if (
      restoreTabHint !== 'analytical' &&
      target?.sensorId &&
      String(sensorId) !== String(target.sensorId)
    ) {
      return;
    }
    if (target?.assetId && String(assetId) !== String(target.assetId)) return;
    if (target?.plantId && String(plantId) !== String(target.plantId)) return;
    if (target?.dateFrom && dateFrom !== target.dateFrom) return;
    if (target?.dateTo && dateTo !== target.dateTo) return;

    const assetMeta = assets.find((a) => String(a.asstId) === String(assetId));
    if (!assetMeta?.asstTag) return;
    if (tab !== 'analytical') {
      const sensorMeta = sensors.find((s) => String(s.astioId) === String(sensorId));
      const telemetrySensor =
        sensorMeta?.astioDeviceId || sensorMeta?.astioExMeterCode;
      if (!telemetrySensor) return;
    }

    const t = window.setTimeout(() => {
      if (!pendingAutoApplyRef.current) return;
      pendingAutoApplyRef.current = false;
      lastAppliedTargetRef.current = null;
      hasAppliedRef.current = true;
      const restoreTab = target?.tab && TABS.some((x) => x.key === target.tab) ? target.tab : tab;
      loadTelemetry(0, paginationModel.pageSize, restoreTab, { rebuildView: false });
    }, 450);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorId, ctdId, dbCtdId, plantId, assetId, dateFrom, dateTo, loadingMeta, tab, assets, sensors]);

  // If cascade overwrote a saved id, put it back a few times so restore can finish
  useEffect(() => {
    if (!pendingAutoApplyRef.current) return;
    const target = lastAppliedTargetRef.current;
    if (!target || restoreAttemptRef.current >= 8) return;
    let changed = false;
    if (target.plantId && plantId && String(plantId) !== String(target.plantId) && !loadingMeta) {
      setPlantId(String(target.plantId));
      changed = true;
    }
    if (target.assetId && assetId && String(assetId) !== String(target.assetId) && !loadingMeta) {
      setAssetId(String(target.assetId));
      changed = true;
    }
    if (target.sensorId && sensorId && String(sensorId) !== String(target.sensorId) && !loadingMeta) {
      setSensorId(String(target.sensorId));
      changed = true;
    }
    if (target.dateFrom && dateFrom !== target.dateFrom) {
      setDateFrom(target.dateFrom);
      changed = true;
    }
    if (target.dateTo && dateTo !== target.dateTo) {
      setDateTo(target.dateTo);
      changed = true;
    }
    if (changed) restoreAttemptRef.current += 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId, assetId, sensorId, dateFrom, dateTo, loadingMeta]);

  useEffect(() => {
    if (!hasAppliedRef.current) return;
    loadTelemetry(paginationModel.page, paginationModel.pageSize, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    if (!hasAppliedRef.current) return;
    // Clear previous tab rows immediately so DataGrid cannot paint stale cells under new columns
    setTabRows([]);
    setTabTotal(0);
    setSelectedRow(null);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    if (paginationModel.page === 0) {
      loadTelemetry(0, paginationModel.pageSize, tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const selectedAsset = useMemo(
    () => assets.find((a) => String(a.asstId) === String(assetId)),
    [assets, assetId]
  );
  const selectedSensor = useMemo(
    () => sensors.find((s) => String(s.astioId) === String(sensorId)),
    [sensors, sensorId]
  );

  const chartPoints = useMemo(() => {
    const fromApi = Array.isArray(chart?.points) ? chart.points : [];
    const series = fromApi.length
      ? fromApi
      : // Fallback: build a thin series from the loaded raw page so the plot is never blank
        rawRows
          .filter((r) => r.timestamp != null && r.value != null)
          .map((r) => ({ timestamp: r.timestamp, value: r.value }));
    // Chart X-axis: earliest left → latest right (table stays newest-first)
    return [...series].sort(
      (a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
    );
  }, [chart, rawRows]);

  const displayUnit = useMemo(() => {
    if (chart?.unit) return String(chart.unit);
    const fromRaw = rawRows.find((r) => r.units != null && r.units !== '');
    return fromRaw?.units ? String(fromRaw.units) : '';
  }, [chart, rawRows]);

  const thresholdSourceLabel = useMemo(() => {
    const src = chart?.thresholdSource;
    if (src === 'safety_threshold') return 'Configured thresholds';
    if (src === 'baseline_derived') return 'Derived from baseline (μ±σ)';
    if (src === 'baseline') return 'Baseline only';
    if (src === 'demo') return 'Demo thresholds';
    return null;
  }, [chart]);

  // Raw grid columns = iotdatamodel.sensor_reading_raw (+ optional mapped cols if present in DB)
  const rawColumns = useMemo(() => {
    const hasMeasurement = rawRows.some((r) => r.measurementType != null && r.measurementType !== '');
    const hasUnits = rawRows.some((r) => r.units != null && r.units !== '');
    // Asset is on sensor_reading_raw — always show the column
    const cols = [
      { field: 'timestampLabel', headerName: 'Timestamp', flex: 1.2, minWidth: 150 },
      {
        field: 'asset',
        headerName: 'Asset',
        flex: 0.7,
        minWidth: 90,
        valueFormatter: (v) => cellOrDash(v),
      },
      {
        field: 'sensor',
        headerName: 'Sensor',
        flex: 0.7,
        minWidth: 90,
        valueFormatter: (v) => cellOrDash(v),
      },
    ];
    if (hasMeasurement) {
      cols.push({
        field: 'measurementType',
        headerName: 'Measurement',
        flex: 0.7,
        minWidth: 90,
        valueFormatter: (v) => cellOrDash(v),
      });
    }
    cols.push({
      field: 'value',
      headerName: 'Value',
      flex: 0.55,
      minWidth: 70,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    });
    if (hasUnits) {
      cols.push({
        field: 'units',
        headerName: 'Units',
        flex: 0.4,
        minWidth: 55,
        valueFormatter: (v) => cellOrDash(v),
      });
    }
    cols.push(
      {
        field: 'quality',
        headerName: 'Quality',
        flex: 0.55,
        minWidth: 78,
        renderCell: (params) => qualityChip(params.value),
        sortable: false,
      },
      {
        field: 'waveformRef',
        headerName: 'Waveform',
        flex: 0.9,
        minWidth: 120,
        renderCell: (params) =>
          params.value ? (
            <Link component="button" variant="body2" underline="hover" onClick={(e) => e.stopPropagation()}>
              {params.value}
            </Link>
          ) : (
            '—'
          ),
      }
    );
    return cols;
  }, [rawRows]);

  const activeTabMeta = TABS.find((t) => t.key === tab) || TABS[0];
  const gridColumns = tab === 'raw' ? rawColumns : DATASET_COLUMNS[tab] || [];
  const gridRows = tab === 'raw' ? rawRows : tabRows;
  const gridTotal = tab === 'raw' ? rawTotal : tabTotal;

  const chartTitle = useMemo(() => {
    const sensorName =
      selectedSensor?.astioDescription || selectedSensor?.label || selectedSensor?.astioExMeterCode || null;
    const metric = chart?.metric ? String(chart.metric) : null;
    const unit = displayUnit || null;
    const parts = [];
    if (sensorName) parts.push(sensorName);
    if (metric && (!sensorName || !String(sensorName).toLowerCase().includes(metric.toLowerCase()))) {
      parts.push(metric);
    }
    let title = parts.length ? parts.join(' — ') : 'Time Series';
    if (unit) title = `${title} (${unit})`;
    return title;
  }, [selectedSensor, chart, displayUnit]);

  const aggregatedChartPoints = useMemo(() => {
    if (tab !== 'aggregated') return [];
    return [...tabRows]
      .filter((r) => r.period_start != null && r.avg != null)
      .sort((a, b) => dayjs(a.period_start).valueOf() - dayjs(b.period_start).valueOf())
      .map((r) => ({ timestamp: r.period_start, value: Number(r.avg) }));
  }, [tab, tabRows]);

  const calculatedFeatureIds = useMemo(() => {
    if (tab !== 'calculated') return [];
    const ids = new Set();
    tabRows.forEach((r) => {
      if (r.feature_id != null && r.feature_id !== '') ids.add(String(r.feature_id));
    });
    return [...ids].sort();
  }, [tab, tabRows]);

  useEffect(() => {
    if (tab !== 'calculated') return;
    if (!calculatedFeatureIds.length) {
      setCalcChartFeatureId('');
      return;
    }
    if (!calcChartFeatureId || !calculatedFeatureIds.includes(calcChartFeatureId)) {
      setCalcChartFeatureId(calculatedFeatureIds[0]);
    }
  }, [tab, calculatedFeatureIds, calcChartFeatureId]);

  const calculatedChartPoints = useMemo(() => {
    if (tab !== 'calculated' || !calcChartFeatureId) return [];
    return [...tabRows]
      .filter(
        (r) =>
          String(r.feature_id) === String(calcChartFeatureId) &&
          (r.timestamp != null || r.ts != null) &&
          r.value != null &&
          !Number.isNaN(Number(r.value))
      )
      .sort(
        (a, b) =>
          dayjs(a.timestamp || a.ts).valueOf() - dayjs(b.timestamp || b.ts).valueOf()
      )
      .map((r) => ({ timestamp: r.timestamp || r.ts, value: Number(r.value) }));
  }, [tab, tabRows, calcChartFeatureId]);

  const showTimeChart = tab === 'raw' || tab === 'aggregated' || tab === 'calculated';
  const { telemetryAssetId, telemetrySensorId } = resolveTelemetryIds();
  const assetLabel = selectedAsset
    ? `${selectedAsset.asstTag || assetId}${selectedAsset.asstDesc ? ` — ${selectedAsset.asstDesc}` : ''}`
    : assetId;
  const sensorLabel =
    tab === 'analytical'
      ? 'All features (asset)'
      : selectedSensor?.astioDescription ||
        selectedSensor?.astioDeviceName ||
        selectedSensor?.astioExMeterCode ||
        sensorId;

  const activeChartPoints =
    tab === 'raw' ? chartPoints : tab === 'aggregated' ? aggregatedChartPoints : calculatedChartPoints;

  const activeChartTitle =
    tab === 'raw'
      ? chartTitle
      : tab === 'aggregated'
        ? 'Aggregated Avg — Time Series'
        : `Calculated Field — ${calcChartFeatureId || 'feature'}`;

  const activeSeriesLabel =
    tab === 'raw' ? 'Actual' : tab === 'aggregated' ? 'Avg' : `Feature ${calcChartFeatureId || ''}`;

  /** Shared details body — docked panel and fullscreen chart both use this */
  const sensorDetailsBody = (
    <>
      <Typography variant="overline" color="text.secondary">
        Sensor details
      </Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5, mb: 2 }}>
        <Typography variant="body2">
          <strong>Asset:</strong> {selectedAsset?.asstTag || '—'}
          {selectedAsset?.asstDesc ? ` — ${selectedAsset.asstDesc}` : ''}
        </Typography>
        <Typography variant="body2">
          <strong>Sensor:</strong>{' '}
          {tab === 'analytical'
            ? 'All features for asset (no sensor filter)'
            : selectedSensor?.astioExMeterCode || selectedSensor?.label || '—'}
        </Typography>
        <Typography variant="body2">
          <strong>Type:</strong>{' '}
          {selectedSensor?.astioExSensorType || selectedSensor?.astioDescription || '—'}
        </Typography>
        {displayUnit ? (
          <Typography variant="body2">
            <strong>Units:</strong> {displayUnit}
          </Typography>
        ) : null}
        {thresholdSourceLabel ? (
          <Typography variant="body2">
            <strong>Thresholds:</strong> {thresholdSourceLabel}
          </Typography>
        ) : null}
      </Stack>

      <Typography variant="overline" color="text.secondary">
        {selectedRow?.selectionSource === 'chart' ? 'Selected point' : 'Record details'}
      </Typography>
      {selectedRow ? (
        <Stack spacing={0.75} sx={{ mt: 0.5, mb: 2 }}>
          <Typography variant="body2">
            <strong>Timestamp:</strong>{' '}
            {selectedRow.timestampLabel ||
              selectedRow.periodStartLabel ||
              selectedRow.tsLabel ||
              '—'}
          </Typography>
          {selectedRow.periodEndLabel ? (
            <Typography variant="body2">
              <strong>Period end:</strong> {selectedRow.periodEndLabel}
            </Typography>
          ) : null}
          {selectedRow.sensor != null && selectedRow.sensor !== '' ? (
            <Typography variant="body2">
              <strong>Sensor:</strong> {selectedRow.sensor}
            </Typography>
          ) : null}
          <Typography variant="body2">
            <strong>Value:</strong>{' '}
            {selectedRow.value != null ? selectedRow.value : '—'}
            {selectedRow.units ? ` ${selectedRow.units}` : ''}
          </Typography>
          {selectedRow.avg != null ? (
            <Typography variant="body2">
              <strong>Avg:</strong> {selectedRow.avg}
            </Typography>
          ) : null}
          {selectedRow.min != null || selectedRow.max != null ? (
            <Typography variant="body2">
              <strong>Min / Max:</strong> {selectedRow.min ?? '—'} / {selectedRow.max ?? '—'}
            </Typography>
          ) : null}
          {selectedRow.stddev != null ? (
            <Typography variant="body2">
              <strong>StdDev:</strong> {selectedRow.stddev}
            </Typography>
          ) : null}
          {selectedRow.p95 != null ? (
            <Typography variant="body2">
              <strong>P95:</strong> {selectedRow.p95}
            </Typography>
          ) : null}
          {selectedRow.z_score != null ? (
            <Typography variant="body2">
              <strong>Z-Score:</strong> {selectedRow.z_score}
            </Typography>
          ) : null}
          {selectedRow.feature_id != null ? (
            <Typography variant="body2">
              <strong>Feature:</strong> {selectedRow.feature_id}
            </Typography>
          ) : null}
          {selectedRow.chartBaseline != null ? (
            <Typography variant="body2">
              <strong>Baseline:</strong> {selectedRow.chartBaseline}
            </Typography>
          ) : null}
          {selectedRow.chartWarning != null ? (
            <Typography variant="body2">
              <strong>Warning:</strong> {selectedRow.chartWarning}
            </Typography>
          ) : null}
          {selectedRow.chartCritical != null ? (
            <Typography variant="body2">
              <strong>Critical:</strong> {selectedRow.chartCritical}
            </Typography>
          ) : null}
          {selectedRow.thresholdStatus ? (
            <Typography
              variant="body2"
              sx={{
                color:
                  selectedRow.thresholdStatus === 'CRITICAL'
                    ? 'error.main'
                    : selectedRow.thresholdStatus === 'WARNING'
                      ? 'warning.main'
                      : 'success.main',
                fontWeight: 600,
              }}
            >
              Status: {selectedRow.thresholdStatus}
            </Typography>
          ) : null}
          {selectedRow.quality != null ? (
            <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <strong>Quality:</strong> {qualityChip(selectedRow.quality)}
            </Typography>
          ) : null}
          {selectedRow.waveformRef ? (
            <Typography variant="body2">
              <strong>Waveform:</strong> {selectedRow.waveformRef}
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Click a chart point or grid row to inspect.
        </Typography>
      )}
    </>
  );

  /** Chart click → nearest grid row (if any) + thresholds → Details panel */
  const handleChartPointClick = useCallback(
    (point) => {
      if (!point?.timestamp) return;
      const clickTs = dayjs(point.timestamp).valueOf();
      if (!Number.isFinite(clickTs)) return;

      const rows = tab === 'raw' ? rawRows : tabRows;
      let nearest = null;
      let nearestDist = Infinity;
      rows.forEach((row) => {
        const rowTs = dayjs(
          row.timestamp || row.period_start || row.ts || row.measured_at
        ).valueOf();
        if (!Number.isFinite(rowTs)) return;
        const dist = Math.abs(rowTs - clickTs);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = row;
        }
      });
      // Prefer match within ~1 bucket (chart buckets can be large)
      const matchOk = nearest && nearestDist <= 2 * 60 * 60 * 1000;

      const value = Number(point.value);
      const baseline = chart?.baseline != null ? Number(chart.baseline) : null;
      const warning = chart?.warningThreshold != null ? Number(chart.warningThreshold) : null;
      const critical = chart?.criticalThreshold != null ? Number(chart.criticalThreshold) : null;
      let thresholdStatus = 'NORMAL';
      if (!Number.isNaN(value)) {
        if (critical != null && !Number.isNaN(critical) && value >= critical) thresholdStatus = 'CRITICAL';
        else if (warning != null && !Number.isNaN(warning) && value >= warning) thresholdStatus = 'WARNING';
      }

      const matched = matchOk ? nearest : null;
      setSelectedRow({
        ...(matched || {}),
        id: matched?.id || `chart-${point.timestamp}`,
        selectionSource: 'chart',
        timestamp: point.timestamp,
        timestampLabel: (() => {
          const d = dayjs(point.timestamp);
          if (!d.isValid()) return '—';
          // Show ms when the reading is not on a whole second (common with duplicates)
          return d.millisecond() ? d.format('YYYY-MM-DD HH:mm:ss.SSS') : d.format('YYYY-MM-DD HH:mm:ss');
        })(),
        value: Number.isNaN(value) ? point.value : value,
        units: matched?.units ?? chart?.unit ?? displayUnit ?? '',
        sensor: matched?.sensor ?? null,
        quality: matched?.quality ?? null,
        waveformRef: matched?.waveformRef ?? null,
        // Aggregated / calculated extras when matched
        min: matched?.min ?? matched?.agg_min ?? null,
        max: matched?.max ?? matched?.agg_max ?? null,
        avg: matched?.avg ?? matched?.agg_avg ?? null,
        stddev: matched?.stddev ?? null,
        p95: matched?.p95 ?? null,
        periodStartLabel: matched?.periodStartLabel ?? null,
        periodEndLabel: matched?.periodEndLabel ?? null,
        z_score: matched?.z_score ?? null,
        feature_id: matched?.feature_id ?? null,
        chartBaseline: baseline,
        chartWarning: warning,
        chartCritical: critical,
        thresholdStatus,
      });
      setPanelOpen(true);
    },
    [tab, rawRows, tabRows, chart, displayUnit]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        maxHeight: 'calc(100vh - 64px)',
        p: 0.75,
        pb: 1,
        gap: 0.75,
        overflow: 'hidden',
        boxSizing: 'border-box',
        bgcolor: '#f7f8fa',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: '0.95rem' }}>
            IoT Data Grid Viewer
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Explore sensor data, features, baselines and trends
          </Typography>
        </Box>
        <Button
          size="small"
          variant={panelOpen ? 'contained' : 'outlined'}
          startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => setPanelOpen((o) => !o)}
          sx={{ textTransform: 'none', py: 0.25, px: 1, fontSize: '0.75rem', minHeight: 28 }}
        >
          {panelOpen ? 'Hide details' : 'Details'}
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" onClose={() => setError('')} sx={{ flexShrink: 0, py: 0 }}>
          {error}
        </Alert>
      )}

      {/* Filters — one compact scope row + time row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          px: 1,
          py: 0.6,
          flexShrink: 0,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            gap: 0.6,
            alignItems: 'end',
          }}
        >
          <FormControl size="small" fullWidth sx={compactFieldSx}>
            <InputLabel>AsroTrack</InputLabel>
            <Select
              label="AsroTrack"
              value={ctdId}
              onChange={(e) => {
                setCtdId(e.target.value);
              }}
            >
              {connectors.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.mcName} ({c.status || '—'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={compactFieldSx}>
            <InputLabel>IoT database</InputLabel>
            <Select
              label="IoT database"
              value={dbCtdId}
              onChange={(e) => {
                setDbCtdId(e.target.value);
              }}
            >
              {pgConnectors.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.description || c.mcName} ({c.status || '—'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth sx={compactFieldSx}>
            <InputLabel>Plant</InputLabel>
            <Select
              label="Plant"
              value={
                plants.some((p) => String(p.plantId) === String(plantId)) ? plantId : ''
              }
              onChange={(e) => {
                setPlantId(e.target.value);
              }}
              disabled={loadingMeta || !plants.length}
            >
              {plants.map((p) => (
                <MenuItem key={String(p.plantId)} value={String(p.plantId)}>
                  {p.label || p.plantCode || p.plantId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            fullWidth
            options={assets}
            loading={loadingMeta}
            disabled={loadingMeta || !plantId || !assets.length}
            value={assets.find((a) => String(a.asstId) === String(assetId)) || null}
            onChange={(_, next) => {
              setAssetId(next ? String(next.asstId) : '');
            }}
            getOptionLabel={(a) => {
              if (!a) return '';
              const tag = a.asstTag || a.asstId || '';
              return a.asstDesc ? `${tag} — ${a.asstDesc}` : String(tag);
            }}
            isOptionEqualToValue={(a, b) => String(a?.asstId) === String(b?.asstId)}
            filterOptions={(options, { inputValue }) => {
              const q = String(inputValue || '')
                .trim()
                .toLowerCase();
              if (!q) return options;
              return options.filter((a) => {
                const tag = String(a.asstTag || '').toLowerCase();
                const desc = String(a.asstDesc || '').toLowerCase();
                const id = String(a.asstId || '').toLowerCase();
                return tag.includes(q) || desc.includes(q) || id.includes(q);
              });
            }}
            noOptionsText={plantId ? 'No matching assets' : 'Select a plant first'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Asset"
                placeholder="Search asset…"
                sx={compactFieldSx}
              />
            )}
            sx={{
              ...compactFieldSx,
              minWidth: 0,
              '& .MuiAutocomplete-inputRoot': {
                height: CTRL_H_PX,
                minHeight: `${CTRL_H}px !important`,
                maxHeight: CTRL_H_PX,
                py: '0 !important',
                fontSize: '0.75rem',
              },
            }}
            ListboxProps={{ style: { maxHeight: 280, fontSize: '0.75rem' } }}
          />
          <FormControl size="small" fullWidth sx={compactFieldSx}>
            <InputLabel>Sensor</InputLabel>
            <Select
              label="Sensor"
              value={
                sensors.some((s) => String(s.astioId) === String(sensorId))
                  ? sensorId
                  : ''
              }
              onChange={(e) => {
                setSensorId(e.target.value);
              }}
              disabled={
                loadingMeta || !sensors.length || tab === 'analytical'
              }
            >
              {sensors.map((s) => (
                <MenuItem key={s.astioId} value={String(s.astioId)}>
                  {s.label || s.astioDescription || s.astioId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Row 2: every column = same label slot height + same CTRL_H control */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 0.75,
            rowGap: 1,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: 0.75,
              minWidth: 0,
              flex: '1 1 auto',
            }}
          >
            {/* Quick ranges — invisible label slot keeps stack height = From/Saved */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: { xs: '100%', sm: 320 },
                maxWidth: { sm: 420 },
                flex: { xs: '1 1 100%', sm: '1 1 320px' },
                boxSizing: 'border-box',
              }}
            >
              <Box component="span" sx={FILTER_LABEL_SLOT_SX} aria-hidden>
                .
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(72px, 1fr))',
                  gap: 0.6,
                  height: CTRL_H_PX,
                  minHeight: CTRL_H_PX,
                  maxHeight: CTRL_H_PX,
                  boxSizing: 'border-box',
                }}
              >
                {PRESETS.map((p) => (
                  <Button
                    key={p.key}
                    size="small"
                    fullWidth
                    variant={preset === p.key ? 'contained' : 'outlined'}
                    onClick={() => applyPreset(p.key)}
                    sx={{ ...ctrlBtnSx, minWidth: 0, px: 0.5 }}
                  >
                    {p.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* From */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 150,
                flex: '1 1 150px',
                maxWidth: 220,
                boxSizing: 'border-box',
              }}
            >
              <Typography component="label" sx={FILTER_LABEL_SX} htmlFor="iot-filter-from">
                From
              </Typography>
              <TextField
                id="iot-filter-from"
                size="small"
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => {
                  setPreset('custom');
                  setDateFrom(e.target.value);
                }}
                fullWidth
                sx={timeFieldSx}
                inputProps={{ 'aria-label': 'From' }}
              />
            </Box>

            {/* To */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 150,
                flex: '1 1 150px',
                maxWidth: 220,
                boxSizing: 'border-box',
              }}
            >
              <Typography component="label" sx={FILTER_LABEL_SX} htmlFor="iot-filter-to">
                To
              </Typography>
              <TextField
                id="iot-filter-to"
                size="small"
                type="datetime-local"
                value={dateTo}
                onChange={(e) => {
                  setPreset('custom');
                  setDateTo(e.target.value);
                }}
                fullWidth
                sx={timeFieldSx}
                inputProps={{ 'aria-label': 'To' }}
              />
            </Box>

            {/* Saved */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: { xs: '100%', sm: 180 },
                maxWidth: 200,
                minWidth: 140,
                flexShrink: 0,
                boxSizing: 'border-box',
              }}
            >
              <Typography component="label" sx={FILTER_LABEL_SX} id="iot-filter-saved-label">
                Saved
              </Typography>
              <FormControl
                size="small"
                fullWidth
                sx={{
                  ...compactFieldSx,
                  height: CTRL_H_PX,
                  minHeight: CTRL_H_PX,
                  maxHeight: CTRL_H_PX,
                }}
              >
                <Select
                  labelId="iot-filter-saved-label"
                  value={
                    !selectedPresetId ||
                    filterPresets.some((x) => x.id === selectedPresetId)
                      ? selectedPresetId
                      : ''
                  }
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          None
                        </Box>
                      );
                    }
                    const presetItem = filterPresets.find((x) => x.id === selected);
                    return presetItem?.name || selected;
                  }}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedPresetId(id);
                    const presetItem = filterPresets.find((x) => x.id === id);
                    if (presetItem) applyFilterPreset(presetItem);
                  }}
                  MenuProps={{
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        minWidth: 280,
                        maxWidth: 420,
                        maxHeight: 280,
                      },
                    },
                  }}
                  sx={{
                    height: CTRL_H_PX,
                    minHeight: `${CTRL_H}px !important`,
                    maxHeight: CTRL_H_PX,
                    boxSizing: 'border-box',
                    '& .MuiSelect-select': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      pr: 3,
                      py: 0,
                      height: CTRL_H_PX,
                      display: 'flex',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                    },
                  }}
                  inputProps={{ 'aria-label': 'Saved filter' }}
                >
                  <MenuItem value="" dense sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                    None
                  </MenuItem>
                  {filterPresets.map((p) => (
                    <MenuItem
                      key={p.id}
                      value={p.id}
                      dense
                      sx={{
                        fontSize: '0.75rem',
                        whiteSpace: 'normal',
                        lineHeight: 1.35,
                        py: 0.75,
                        alignItems: 'flex-start',
                      }}
                    >
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Actions — same label slot + CTRL_H so stack height matches */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              ml: { xs: 0, md: 'auto' },
              boxSizing: 'border-box',
            }}
          >
            <Box component="span" sx={FILTER_LABEL_SLOT_SX} aria-hidden>
              .
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 0.6,
                height: CTRL_H_PX,
                minHeight: CTRL_H_PX,
                maxHeight: CTRL_H_PX,
                boxSizing: 'border-box',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={openSavePresetDialog}
                title="Save current filters"
                sx={{ ...ctrlBtnSx, minWidth: 48, px: 0.75 }}
              >
                Save
              </Button>
              <Button
                size="small"
                variant="text"
                color="inherit"
                disabled={!selectedPresetId}
                onClick={deleteSelectedPreset}
                title="Delete saved filter"
                sx={{ ...ctrlBtnSx, minWidth: 28, px: 0.4 }}
              >
                ✕
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={onApply}
                disabled={loadingData || processingRaw || !sensorId}
                sx={{ ...ctrlBtnSx, minWidth: 80, px: 1.75 }}
              >
                {loadingData ? <CircularProgress size={14} color="inherit" /> : 'Apply'}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={onProcessFromRaw}
                disabled={processingRaw || loadingData || !dbCtdId}
                title="Clear and rebuild analytics tables from sensor_reading_raw on the selected IoT database"
                sx={{
                  ...ctrlBtnSx,
                  minWidth: 148,
                  px: 1.5,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  color: '#5c0a0a',
                  bgcolor: '#fff',
                  border: '2px solid #8B1E1E',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#8B1E1E',
                    color: '#fff',
                    borderColor: '#8B1E1E',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(0,0,0,0.45)',
                    bgcolor: '#f3f3f3',
                    borderColor: 'rgba(0,0,0,0.28)',
                    opacity: 1,
                  },
                }}
              >
                {processingRaw ? (
                  <>
                    <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} />
                    Processing…
                  </>
                ) : (
                  'Process sensor data'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 30,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRadius: 1,
          px: 0.5,
          '& .MuiTabs-indicator': { height: 2 },
        }}
      >
        {TABS.map((t) => (
          <Tab
            key={t.key}
            value={t.key}
            label={t.label}
            sx={{ minHeight: 30, py: 0.25, px: 1.25, textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}
          />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {showTimeChart && (
            <Box
              sx={{
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  py: 0.15,
                  cursor: 'pointer',
                  flexShrink: 0,
                  minHeight: 30,
                  gap: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => setChartExpanded((v) => !v)}
                role="button"
                aria-expanded={chartExpanded}
                aria-label={chartExpanded ? 'Collapse chart' : 'Expand chart'}
              >
                <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                  {activeChartTitle}
                  {tab === 'raw' && chart?.source === 'demo' ? ' (demo)' : ''}
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                  {tab === 'calculated' && calculatedFeatureIds.length > 0 ? (
                    <FormControl
                      size="small"
                      sx={{ minWidth: 88 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Select
                        value={calcChartFeatureId || calculatedFeatureIds[0]}
                        onChange={(e) => setCalcChartFeatureId(e.target.value)}
                        displayEmpty
                        sx={{
                          height: 26,
                          fontSize: '0.72rem',
                          '& .MuiSelect-select': { py: 0.25, px: 1 },
                        }}
                        inputProps={{ 'aria-label': 'Feature for chart' }}
                      >
                        {calculatedFeatureIds.map((fid) => (
                          <MenuItem key={fid} value={fid} sx={{ fontSize: '0.75rem' }}>
                            {fid}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                  {tab === 'raw' && thresholdSourceLabel ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}
                      title={thresholdSourceLabel}
                    >
                      {thresholdSourceLabel}
                    </Typography>
                  ) : null}
                  <IconButton
                    size="small"
                    title="View chart fullscreen"
                    onClick={(e) => {
                      e.stopPropagation();
                      setChartFullscreen(true);
                    }}
                    aria-label="View chart fullscreen"
                  >
                    <OpenInFullIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    title={chartExpanded ? 'Collapse chart' : 'Expand chart'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setChartExpanded((v) => !v);
                    }}
                    aria-label={chartExpanded ? 'Collapse chart' : 'Expand chart'}
                  >
                    {chartExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Stack>
              </Box>
              {chartExpanded && (
                <Box sx={{ px: 0.75, pb: 0.5, height: 168, overflow: 'hidden' }}>
                  <SimpleLineChart
                    points={activeChartPoints}
                    baseline={tab === 'raw' ? chart?.baseline : undefined}
                    warningThreshold={tab === 'raw' ? chart?.warningThreshold : undefined}
                    criticalThreshold={tab === 'raw' ? chart?.criticalThreshold : undefined}
                    unit={tab === 'raw' ? displayUnit : ''}
                    height={158}
                    compact
                    seriesLabel={activeSeriesLabel}
                    onPointClick={handleChartPointClick}
                    selectedTimestamp={
                      selectedRow?.selectionSource === 'chart'
                        ? selectedRow.timestamp
                        : selectedRow?.timestamp || selectedRow?.period_start || null
                    }
                    selectedValue={selectedRow?.value ?? null}
                  />
                </Box>
              )}
            </Box>
          )}

          {tab === 'vibration' ? (
            <VibrationAnalysisPanel
              assetId={assetId}
              sensorId={sensorId}
              sensors={sensors}
              telemetryAssetId={telemetryAssetId}
              telemetrySensorId={telemetrySensorId}
              dbCtdId={dbCtdId}
              dateFrom={toIsoParam(dateFrom)}
              dateTo={toIsoParam(dateTo)}
              assetLabel={assetLabel}
              sensorLabel={sensorLabel}
            />
          ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 220,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 1,
                py: 0.35,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fafbfc',
              }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem' }}>
                {activeTabMeta.label}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontSize: '0.65rem' }}>
                  ({tabTable || activeTabMeta.table})
                  {tab === 'analytical' && analyticalViewMeta
                    ? ` · sp_get ${analyticalViewMeta.from} → ${analyticalViewMeta.to}`
                    : ''}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {gridTotal.toLocaleString()} records
                {gridTotal > 0
                  ? ` · page ${paginationModel.page + 1} of ${Math.max(1, Math.ceil(gridTotal / paginationModel.pageSize))} · ${paginationModel.pageSize}/page`
                  : ''}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <DataGrid
                key={`iot-grid-${tab}`}
                rows={gridRows}
                columns={gridColumns}
                getRowId={(row) => row.id}
                pagination
                paginationMode="server"
                rowCount={gridTotal}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
                density="compact"
                columnHeaderHeight={32}
                rowHeight={34}
                disableRowSelectionOnClick
                hideFooter={false}
                hideFooterSelectedRowCount
                onRowClick={(params) => {
                  setSelectedRow(params.row);
                  setPanelOpen(true);
                }}
                loading={loadingData}
                sx={{
                  border: 0,
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  fontSize: '0.72rem',
                  display: 'flex',
                  flexDirection: 'column',
                  '--DataGrid-rowBorderColor': 'rgba(0, 0, 0, 0.06)',
                  '& .MuiDataGrid-columnHeaders': {
                    minHeight: '32px !important',
                    maxHeight: '32px !important',
                    bgcolor: '#f5f6f8',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    borderRight: 'none !important',
                    px: 1.25,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    letterSpacing: 0.1,
                  },
                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-iconSeparator': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-row': {
                    maxHeight: '34px !important',
                    minHeight: '34px !important',
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.72rem',
                    py: 0,
                    px: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    lineHeight: '34px',
                    borderRight: 'none !important',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                  },
                  '& .MuiDataGrid-cellContent': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    overflowX: 'auto',
                  },
                  '& .MuiDataGrid-main': {
                    flex: '1 1 0 !important',
                    minHeight: '0 !important',
                    overflow: 'hidden',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: 40,
                    height: 40,
                    flex: '0 0 40px',
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                    bgcolor: '#fafbfc',
                    px: 0.5,
                    justifyContent: 'flex-end',
                  },
                  '& .MuiTablePagination-root': {
                    overflow: 'visible',
                    width: 'auto',
                    ml: 'auto',
                  },
                  '& .MuiTablePagination-toolbar': {
                    minHeight: 40,
                    pl: 0.5,
                    pr: 0.5,
                    justifyContent: 'flex-end',
                  },
                  '& .MuiTablePagination-spacer': {
                    display: 'none',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    margin: 0,
                    fontSize: '0.72rem',
                  },
                  '& .MuiTablePagination-select': {
                    fontSize: '0.72rem',
                  },
                  '& .MuiDataGrid-row.Mui-selected': { bgcolor: 'rgba(139, 21, 56, 0.06)' },
                  '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(21, 101, 192, 0.04)' },
                }}
              />
            </Box>
            {tab === 'baseline' && baselineFeatures.length > 0 && (
              <Box sx={{ maxHeight: 160, overflow: 'auto', borderTop: '1px solid', borderColor: 'divider', px: 1.5, py: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">
                  baseline_feature ({baselineFeatures.length})
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {baselineFeatures.map((bf) => (
                    <Typography key={bf.baseline_feature_id} variant="caption">
                      {bf.baseline_feature_id}: feature {bf.feature_id} · mean {bf.mean} · p95 {bf.p95} ·{' '}
                      {bf.direction}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
          )}
        </Box>

        {/* Docked details panel — closed by default; opens like the mockup */}
        <Box
          sx={{
            width: panelOpen ? 300 : 0,
            minWidth: panelOpen ? 300 : 0,
            opacity: panelOpen ? 1 : 0,
            overflow: 'hidden',
            transition: 'width 0.2s ease, min-width 0.2s ease, opacity 0.15s ease',
            height: '100%',
            border: panelOpen ? '1px solid' : 'none',
            borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Details
            </Typography>
            <IconButton size="small" onClick={() => setPanelOpen(false)} aria-label="Close details">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ p: 1.5, overflow: 'auto' }}>{sensorDetailsBody}</Box>
        </Box>
      </Box>

      <Dialog
        fullScreen
        open={chartFullscreen}
        onClose={() => setChartFullscreen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#f7f8fa',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            gap: 1,
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
            {activeChartTitle}
            {tab === 'raw' && chart?.source === 'demo' ? ' (demo)' : ''}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            {tab === 'calculated' && calculatedFeatureIds.length > 0 ? (
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={calcChartFeatureId || calculatedFeatureIds[0]}
                  onChange={(e) => setCalcChartFeatureId(e.target.value)}
                  displayEmpty
                  sx={{
                    height: 32,
                    fontSize: '0.8rem',
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  }}
                  inputProps={{ 'aria-label': 'Feature for chart' }}
                >
                  {calculatedFeatureIds.map((fid) => (
                    <MenuItem key={fid} value={fid} sx={{ fontSize: '0.8rem' }}>
                      {fid}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {tab === 'raw' && thresholdSourceLabel ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
              >
                {thresholdSourceLabel}
              </Typography>
            ) : null}
            <Button
              size="small"
              variant={panelOpen ? 'contained' : 'outlined'}
              startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setPanelOpen((o) => !o)}
              sx={{ textTransform: 'none', py: 0.25, px: 1, fontSize: '0.75rem', minHeight: 28 }}
            >
              {panelOpen ? 'Hide details' : 'Details'}
            </Button>
            <IconButton
              size="small"
              onClick={() => setChartFullscreen(false)}
              title="Exit fullscreen"
              aria-label="Exit fullscreen"
            >
              <CloseFullscreenIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            gap: 1,
            p: 1,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <SimpleLineChart
              points={activeChartPoints}
              baseline={tab === 'raw' ? chart?.baseline : undefined}
              warningThreshold={tab === 'raw' ? chart?.warningThreshold : undefined}
              criticalThreshold={tab === 'raw' ? chart?.criticalThreshold : undefined}
              unit={tab === 'raw' ? displayUnit : ''}
              fill
              compact={false}
              seriesLabel={activeSeriesLabel}
              onPointClick={handleChartPointClick}
              selectedTimestamp={
                selectedRow?.selectionSource === 'chart'
                  ? selectedRow.timestamp
                  : selectedRow?.timestamp || selectedRow?.period_start || null
              }
              selectedValue={selectedRow?.value ?? null}
            />
          </Box>
          <Box
            sx={{
              width: panelOpen ? 320 : 0,
              minWidth: panelOpen ? 320 : 0,
              opacity: panelOpen ? 1 : 0,
              overflow: 'hidden',
              transition: 'width 0.2s ease, min-width 0.2s ease, opacity 0.15s ease',
              height: '100%',
              border: panelOpen ? '1px solid' : 'none',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Details
              </Typography>
              <IconButton size="small" onClick={() => setPanelOpen(false)} aria-label="Close details">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 1.5, overflow: 'auto', flex: 1, minHeight: 0 }}>{sensorDetailsBody}</Box>
          </Box>
        </Box>
      </Dialog>

      <Dialog
        open={savePresetOpen}
        onClose={() => !savingPreset && setSavePresetOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1, fontSize: '1rem' }}>Save filter combination</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            A name is needed so you can pick this setup later from Saved filters. You can keep the
            suggested name or change it.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={savePresetName}
            onChange={(e) => setSavePresetName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmSavePreset();
            }}
            placeholder="e.g. EXT-001 VIB Jul 1–2"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onClick={() => setSavePresetOpen(false)}
            disabled={savingPreset}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={confirmSavePreset}
            disabled={savingPreset}
            sx={{ textTransform: 'none' }}
          >
            {savingPreset ? <CircularProgress size={16} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IoTDataView;
